import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { registerWithEmail, registerWithGoogle } from '../firebase/auth';
import { saveUserToLocal, getAuthFlag } from '../utils/authUser';
import SEO, { seoConfig } from '../component/SEO';
import { toastedSuccess, toastedError } from '../config/toast';
import { title } from '../config/config';

const Register = () => {
     const navigate = useNavigate();

     const [formData, setFormData] = useState({
          name: '',
          email: '',
          password: '',
          confirmPassword: '',
     });

     const [loading, setLoading] = useState(false);
     const [googleLoading, setGoogleLoading] = useState(false);
     const [errorText, setErrorText] = useState('');
     const [showPassword, setShowPassword] = useState(false);
     const [showConfirmPassword, setShowConfirmPassword] = useState(false);

     useEffect(() => {
          if (getAuthFlag()) {
               navigate('/verify');
          }
     }, [navigate]);

     const handleChange = (event) => {
          const { name, value } = event.target;

          setFormData((prev) => ({
               ...prev,
               [name]: value,
          }));
     };

     const getFirebaseError = (error) => {
          switch (error.code) {
               case 'auth/email-already-in-use':
                    return 'This email is already registered.';
               case 'auth/invalid-email':
                    return 'Invalid email address.';
               case 'auth/weak-password':
                    return 'Password should be at least 6 characters.';
               case 'auth/popup-closed-by-user':
                    return 'Google signup popup was closed.';
               default:
                    return error.message || 'Registration failed. Please try again.';
          }
     };

     const handleRegister = async (event) => {
          event.preventDefault();
          setErrorText('');

          if (!formData.name.trim()) {
               setErrorText('Name is required.');
               return;
          }

          if (!formData.email.trim()) {
               setErrorText('Email is required.');
               return;
          }

          if (!formData.password) {
               setErrorText('Password is required.');
               return;
          }

          if (formData.password.length < 6) {
               setErrorText('Password should be at least 6 characters.');
               return;
          }

          if (formData.password !== formData.confirmPassword) {
               setErrorText('Passwords do not match.');
               return;
          }

          try {
               setLoading(true);

               const user = await registerWithEmail(
                    formData.name.trim(),
                    formData.email.trim(),
                    formData.password
               );

               await saveUserToLocal(user);
               navigate('/verify');
               toastedSuccess("Account created 🎉 Complete your profile next");
          } catch (error) {
               console.error('Register error:', error);
               if (error.code === 'auth/email-already-in-use') {
                    toastedError('This email is already registered. Redirecting to login...');
                    setTimeout(() => navigate('/login'), 2000);
               } else {
                    setErrorText(getFirebaseError(error));
               }
          } finally {
               setLoading(false);
          }
     };

     const handleGoogleRegister = async () => {
          setErrorText('');

          try {
               setGoogleLoading(true);

               const { user, isNewUser } = await registerWithGoogle();

               if (!isNewUser) {
                    toastedError('This account is already registered. Redirecting to login...');
                    setTimeout(() => navigate('/login'), 2000);
                    return;
               }

               await saveUserToLocal(user);
               navigate('/verify');
               toastedSuccess("Account created 🎉 Complete your profile next");
          } catch (error) {
               console.error('Google register error:', error);
               setErrorText(getFirebaseError(error));
          } finally {
               setGoogleLoading(false);
          }
     };

     return (
          <>

               <SEO {...seoConfig.register} />

               <div className="login-hero-theme">
                    <div className="floating-elements">
                         <div className="el sphere" />
                         <div className="el cube" />
                         <div className="el torus" />
                         <div className="el triangle" />
                    </div>

                    <main className="login-card-v2">
                         <div className="badge">Join {title}</div>

                         <header className="login-intro">
                              <h1>
                                   Create your <span>account.</span>
                              </h1>
                              <p>
                                   Start saving ideas, sharing concepts, and building with collaborators.
                              </p>
                         </header>

                         {errorText && <div className="login-error text-danger">{errorText}</div>}

                         <form className="login-form-box" onSubmit={handleRegister}>
                              <div className="input-group">
                                   <label>Full Name</label>
                                   <input
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        placeholder="Enter your full name"
                                   />
                              </div>

                              <div className="input-group">
                                   <label>Email</label>
                                   <input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        placeholder="Enter your email"
                                   />
                              </div>

                              <div className="input-group">
                                   <label>Password</label>
                                   <div className="input-eye-wrap">
                                        <input
                                             type={showPassword ? 'text' : 'password'}
                                             name="password"
                                             value={formData.password}
                                             onChange={handleChange}
                                             placeholder="Create password"
                                        />
                                        <button type="button" className="eye-toggle" onClick={() => setShowPassword(p => !p)}>
                                             {showPassword ? '🙈' : '👁️'}
                                        </button>
                                   </div>
                              </div>

                              <div className="input-group">
                                   <label>Confirm Password</label>
                                   <div className="input-eye-wrap">
                                        <input
                                             type={showConfirmPassword ? 'text' : 'password'}
                                             name="confirmPassword"
                                             value={formData.confirmPassword}
                                             onChange={handleChange}
                                             placeholder="Confirm password"
                                        />
                                        <button type="button" className="eye-toggle" onClick={() => setShowConfirmPassword(p => !p)}>
                                             {showConfirmPassword ? '🙈' : '👁️'}
                                        </button>
                                   </div>
                              </div>



                              <div className="login-actions">
                                   <button
                                        type="submit"
                                        className="primary-login-btn"
                                        disabled={loading || googleLoading}
                                   >
                                        {loading ? 'Creating...' : 'Create Account'}
                                   </button>

                                   <button
                                        type="button"
                                        className="google-btn-premium"
                                        onClick={handleGoogleRegister}
                                        disabled={loading || googleLoading}
                                   >
                                        <img
                                             src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                                             alt="Google"
                                        />
                                        <span>
                                             {googleLoading ? 'Connecting...' : 'Continue with Google'}
                                        </span>
                                   </button>
                              </div>
                         </form>

                         <div className="auth-bottom-link">
                              Already have an account? <Link to="/login">Login</Link>
                         </div>
                    </main>
               </div>
          </>

     );
};

export default Register;